import { createSignal } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { db, debouncedWrite, reportError } from '~/lib/db';

// Persisted in Supabase, read and written straight from the browser.
//
// This is the source of truth for positions: portfolio-store sums these rows
// into holdings, so recording a buy is what creates or grows a position. There
// is no separate holdings table to fall out of step with.

export type TransactionKind = 'buy' | 'sell';

export interface Transaction {
    id: string;
    kind: TransactionKind;
    /** Plain yyyy-mm-dd, no time or zone. */
    date: string;
    accountId: string;
    symbol: string;
    /** Always positive. The kind carries the sign, so nothing has to guess
        whether a negative figure is a sale or a typo. */
    units: number;
    /** Per unit: paid on a buy, received on a sell. */
    price: number;
    /**
     * Commission and any other per-trade charge, always positive.
     *
     * It adds to the cost base on a buy and comes off the proceeds on a sell.
     * Both directions reduce the gain, which is why it is stored unsigned rather
     * than netted off the price — netted, it would vanish into a number that
     * looks like a fill and the two treatments could not be told apart.
     */
    fee: number;
    /**
     * The loan that paid for this buy, or null when it was paid in cash.
     *
     * This is the whole record of where borrowed money went. Every allocation
     * figure on the Loans page — what a loan put into an account, what is still
     * uninvested, what share of its interest is deductible — is derived from the
     * buys carrying its id, so there is nothing to keep in step with anything.
     *
     * Always null on a sell: a disposal is borrowed money coming back, not going
     * out. What it does do is relieve the borrowed share of the cost base along
     * with the rest of it, pro rata, which positions.ts carries through the walk.
     */
    loanId: string | null;
    createdAt?: string;
}

/** What the trade came to: the money out on a buy, the money in on a sell. */
export const transactionTotal = (t: Transaction): number => (t.kind === 'sell' ? t.units * t.price - t.fee : t.units * t.price + t.fee);

export const KIND_LABEL: Record<TransactionKind, string> = { buy: 'Buy', sell: 'Sell' };

const [transactions, setTransactions] = createStore<Transaction[]>([]);

const [loading, setLoading] = createSignal(true);
const [error, setError] = createSignal<string | null>(null);

export { transactions, loading, error };

// Newest first, and stable: two buys entered on the same date keep their
// insertion order rather than shuffling between reads.
const ORDER = 'ORDER BY date DESC, created_at DESC, id DESC';

const toClient = (r: Record<string, unknown>): Transaction => ({
    id: String(r.id),
    kind: r.kind === 'sell' ? 'sell' : 'buy',
    date: String(r.date),
    accountId: String(r.account_id),
    symbol: String(r.symbol),
    units: Number(r.units),
    price: Number(r.price),
    fee: Number(r.fee),
    loanId: r.loan_id == null ? null : String(r.loan_id),
    createdAt: String(r.created_at),
});

export const loadTransactions = async (): Promise<void> => {
    setLoading(true);
    try {
        // No user filter: row-level security scopes this to the session, so a
        // missing clause cannot leak another user's blotter.
        const res = await db.from('transactions').select('*').order('date', { ascending: false }).order('created_at', { ascending: false });
        if (res.error) throw new Error(res.error.message);
        setTransactions((res.data ?? []).map((r) => toClient(r as Record<string, unknown>)));
        setError(null);
    } catch (e) {
        reportError('loading transactions', e);
        setError(e instanceof Error ? e.message : 'could not load transactions');
    } finally {
        setLoading(false);
    }
};

export type NewTrade = Omit<Transaction, 'id' | 'createdAt'>;

/**
 * Writes through rather than optimistically: the row the table shows is the row
 * the database returned, id and all. A trade that fails to save leaves nothing
 * behind on screen to suggest it was recorded.
 *
 * `accountId` must be a real row id, and so must `loanId` when there is one.
 * Neither accounts nor loans are stored until written to, so the caller resolves
 * both through portfolio-store first — done there rather than here because it
 * already imports this module, and importing back would close a cycle.
 */
export const addTrade = async (entry: NewTrade): Promise<boolean> => {
    try {
        // `user_id` is not sent: the column defaults to auth.uid(), so the
        // database decides whose row this is rather than the browser claiming.
        const res = await db
            .from('transactions')
            .insert({
                id: crypto.randomUUID(),
                kind: entry.kind,
                date: entry.date,
                account_id: entry.accountId,
                symbol: entry.symbol,
                units: entry.units,
                price: entry.price,
                fee: entry.fee,
                // Belt and braces with the database's own check: a sale is
                // borrowed money coming back, and naming a loan on one would
                // read as it going out again.
                loan_id: entry.kind === 'sell' ? null : entry.loanId,
            })
            .select()
            .single();
        if (res.error) throw new Error(res.error.message);
        const saved = toClient(res.data as Record<string, unknown>);
        setTransactions(produce((list) => void list.unshift(saved)));
        setError(null);
        return true;
    } catch (e) {
        reportError(`saving ${entry.kind}`, e);
        setError(e instanceof Error ? e.message : `could not save ${entry.kind}`);
        return false;
    }
};

/**
 * Correct a recorded buy.
 *
 * A field at a time, on a debounce keyed to the row and the column, so typing
 * into a price stays instant and only the last value is written — the same
 * pattern the portfolio store uses.
 *
 * The store is updated first and the database after. If the write fails the
 * error surfaces in the panel; it does not roll the field back, because the
 * value on screen is what you typed and silently reverting it is how an edit
 * gets lost without anyone noticing.
 */
export const updateTransaction = <K extends keyof Transaction>(id: string, field: K, value: Transaction[K]): void => {
    const at = transactions.findIndex((t) => t.id === id);
    if (at === -1) return;
    setTransactions(at, field, value);

    const COLUMN: Partial<Record<keyof Transaction, string>> = { kind: 'kind', date: 'date', accountId: 'account_id', symbol: 'symbol', units: 'units', price: 'price', fee: 'fee', loanId: 'loan_id' };
    const column = COLUMN[field];
    if (column == null) return;

    debouncedWrite(`transaction:${id}:${column}`, async () => {
        const { error: failed } = await db.from('transactions').update({ [column]: value }).eq('id', id);
        if (failed) throw new Error(failed.message);
    });
};

/**
 * Correct a trade's direction.
 *
 * Its own writer rather than a `kind` passed to updateTransaction, because a
 * sell may not name a loan and the database enforces that. The two columns have
 * to move in one statement: as two debounced field writes they race, and one of
 * the two orders is rejected outright.
 */
export const setKind = (id: string, kind: TransactionKind): void => {
    const at = transactions.findIndex((t) => t.id === id);
    if (at === -1) return;
    const loanId = kind === 'sell' ? null : transactions[at].loanId;
    setTransactions(at, { kind, loanId });

    debouncedWrite(`transaction:${id}:kind`, async () => {
        const { error: failed } = await db.from('transactions').update({ kind, loan_id: loanId }).eq('id', id);
        if (failed) throw new Error(failed.message);
    });
};

export const removeTransaction = async (id: string): Promise<void> => {
    try {
        const res = await db.from('transactions').delete().eq('id', id);
        if (res.error) throw new Error(res.error.message);
        setTransactions(
            produce((list) => {
                const index = list.findIndex((t) => t.id === id);
                if (index >= 0) list.splice(index, 1);
            }),
        );
        setError(null);
    } catch (e) {
        reportError('deleting transaction', e);
        setError(e instanceof Error ? e.message : 'could not delete');
    }
};

const CSV_COLUMNS = ['id', 'kind', 'date', 'accountId', 'symbol', 'units', 'price', 'fee', 'createdAt'] as const;

const csvCell = (value: unknown): string => {
    const text = value == null ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/** Built in the browser now that there is no server to serve a file. Takes the
    rows to write so the export matches what the pane is showing. */
export const downloadCsv = (rows: readonly Transaction[]): void => {
    const body = [CSV_COLUMNS.join(','), ...rows.map((t) => CSV_COLUMNS.map((c) => csvCell(t[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.csv';
    a.click();
    URL.revokeObjectURL(url);
};
